import { IDed } from 'gql/Request';
import { PaginationResult, SingletonResult } from 'gql/Common';
import { LITE_REQUEST_ACCESS } from 'gql/TargetGroup';
import Helper from 'utils/Helper';
import BaseTarget from './BaseTarget';

export interface EosService extends BaseTarget {
  namespace: string;
  clusterInstanceName: string;
}

export const LITE_EOS_SERVICE = `
{
  id
  name
  description
  tier
  namespace
  createdBy
  createdAt
  updatedBy
  updatedAt
  deleted
}`;

export const FULL_EOS_SERVICE = `
{
  id
  name
  namespace
  clusterInstanceName
  description
  tier
  tags {
    key
    value
  }
  approvalSlackChannel
  notifyAutoApproval
  enablePaging
  teamRole
  managersADGroups
  orgLeaderRole
  limitedAccessRoles
  accessRequiresBastion
  accessConfiguration {
    level
    access ${LITE_REQUEST_ACCESS}
  }
  createdBy
  createdAt
  updatedBy
  updatedAt
  deleted
}`;

export const LIST_EOS_SERVICE = `
  query listEOSServices($filter: ListEOSServicesFilterInput, $pagination: PaginationInput) {
    listEOSServices (filter: $filter, pagination: $pagination) {
      items ${LITE_EOS_SERVICE}
      total
      nextToken
    }
  }
`;

export interface ListEosServiceOptions {
  size?: number;
  nextToken?: string;
  name?: string;
  clusterInstanceName?: string;
  namespace?: string;
}

export const listEosServices = async (options?: ListEosServiceOptions): Promise<PaginationResult<EosService>> => {
  const result = await Helper.execGQL<PaginationResult<EosService>>({
    query: LIST_EOS_SERVICE,
    variables: {
      filter: {
        name: options?.name,
        clusterInstanceName: options?.clusterInstanceName,
        namespace: options?.namespace,
      },
      pagination: {
        size: options?.size,
        nextToken: options?.nextToken,
      },
    },
  });
  result.listEOSServices.items = result.listEOSServices.items.filter((e) => !e.deleted);
  return result.listEOSServices;
};

export const GET_EOS_SERVICE = `
  query getEOSService($id: String!) {
    getEOSService(id: $id) {
      item ${FULL_EOS_SERVICE}
    }
  }`;

export const getEosService = async (id: string): Promise<EosService> => {
  const result = await Helper.execGQL<SingletonResult<EosService>>({
    query: GET_EOS_SERVICE,
    variables: {
      id,
    },
  });
  return result.getEOSService.item;
};

const CREATE_EOS_SERVICE = `
  mutation createEOSService($create: CreateEOSServiceInput!) {
    createEOSService(create: $create) {
      item {
        id
      }
    } 
  }`;

export const createEosService = async (service: EosService): Promise<IDed> => {
  const result = await Helper.execGQL<SingletonResult<EosService>>({
    query: CREATE_EOS_SERVICE,
    variables: {
      create: service,
    },
  });
  return result.createEOSService.item;
};

const UPDATE_EOS_SERVICE = `
  mutation updateEOSService($update: UpdateEOSServiceInput!) {
    updateEOSService(update: $update) {
      item { 
        id
      }
    }
  }`;

export const updateEosService = async (service: EosService): Promise<IDed> => {
  const result = await Helper.execGQL<SingletonResult<EosService>>({
    query: UPDATE_EOS_SERVICE,
    variables: {
      update: service,
    },
  });
  return result.updateEOSService.item;
};

const DELETE_EOS_SERVICE = `
mutation deleteEOSService($id: String!) {
  deleteEOSService(id: $id) {
      item {
          id
      }
  }
} `;

export const deleteEosService = async (id: string): Promise<IDed> => {
  const result = await Helper.execGQL<SingletonResult<EosService>>({
    query: DELETE_EOS_SERVICE,
    variables: {
      id,
    },
  });
  return result.deleteEOSService.item;
};
